import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, Mail, Download, MapPin, Clock, 
  User, Truck, CheckCircle, Printer, FileText, Package, Camera, Upload, Trash2, Plus
} from "lucide-react";

const LOGO_URL = "/nassau-logo.png";

export default function DeliveryDocket() {
  const { proofId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [proof, setProof] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Dynamic terminology based on job type
  const isCollection = job?.job_type === "collection";
  const jobTypeLabel = isCollection ? "Collection" : "Delivery";
  const addressLabel = isCollection ? "Collection Address" : "Delivery Address";
  const detailsLabel = isCollection ? "Collection Details" : "Delivery Details";
  const completedByLabel = isCollection ? "Collected By" : "Delivered By";
  const itemsLabel = isCollection ? "Items Collected" : "Items Delivered";
  const photoLabel = isCollection ? "Collection Photo" : "Delivery Photo";
  const confirmedLabel = isCollection ? "Collection Confirmed" : "Delivery Confirmed";
  const proofLabel = `Proof of ${jobTypeLabel}`;

  useEffect(() => {
    fetchProof();
  }, [proofId]);

  const fetchProof = async () => {
    try {
      const res = await api.get(`/proofs/${proofId}`);
      setProof(res.data);
      
      // Also fetch the job details to get items, instructions, and documents
      if (res.data.job_id) {
        try {
          const jobRes = await api.get(`/jobs/${res.data.job_id}`);
          setJob(jobRes.data);
        } catch (jobError) {
          console.error("Failed to load job details:", jobError);
        }
      }
    } catch (error) {
      toast.error("Failed to load proof");
      navigate(user?.role === "admin" ? "/admin" : "/driver");
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await api.get(`/jobs/${job.id}/documents/${doc.id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !job) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploading(true);
    try {
      await api.post(`/jobs/${job.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Document uploaded");
      // Refresh job data
      const jobRes = await api.get(`/jobs/${job.id}`);
      setJob(jobRes.data);
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !job) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploading(true);
    try {
      await api.post(`/jobs/${job.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Photo uploaded");
      // Refresh job data
      const jobRes = await api.get(`/jobs/${job.id}`);
      setJob(jobRes.data);
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDocumentDelete = async (doc) => {
    if (!confirm("Delete this document?")) return;
    
    try {
      await api.delete(`/jobs/${job.id}/documents/${doc.id}`);
      toast.success("Document deleted");
      const jobRes = await api.get(`/jobs/${job.id}`);
      setJob(jobRes.data);
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handlePhotoDelete = async (photo) => {
    if (!confirm("Delete this photo?")) return;
    
    try {
      await api.delete(`/jobs/${job.id}/photos/${photo.id}`);
      toast.success("Photo deleted");
      const jobRes = await api.get(`/jobs/${job.id}`);
      setJob(jobRes.data);
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const handleEmail = async () => {
    if (!recipientEmail) {
      toast.error("Please enter an email address");
      return;
    }
    
    setSending(true);
    try {
      await api.post("/proofs/email", {
        proof_id: proofId,
        recipient_email: recipientEmail
      });
      toast.success(`${jobTypeLabel} confirmation sent!`);
      setEmailDialogOpen(false);
      setRecipientEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  if (!proof) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600">Proof not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" data-testid="delivery-docket">
      {/* Header - Hidden on print */}
      <header className="glass-header sticky top-0 z-40 border-b border-slate-200 print:hidden">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(user?.role === "admin" ? "/admin" : "/driver")}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <span className="font-semibold text-slate-800">{jobTypeLabel} Docket</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              data-testid="print-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button
              size="sm"
              onClick={() => setEmailDialogOpen(true)}
              className="bg-[#003366] hover:bg-[#002244]"
              data-testid="email-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email
            </Button>
          </div>
        </div>
      </header>

      {/* Docket Content */}
      <main className="max-w-2xl mx-auto px-4 py-8 print:py-0 print:px-0">
        <Card className="bg-white shadow-lg print:shadow-none print:border-0">
          <CardContent className="p-8">
            {/* Company Header */}
            <div className="flex justify-between items-start border-b-2 border-[#003366] pb-6 mb-6">
              <div>
                <img src={LOGO_URL} alt="Nassau Engineering Ltd." className="h-16 mb-2" />
                <p className="text-sm text-slate-600">{proofLabel}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-xl font-bold text-[#003366]">{proof.job_number}</p>
                <p className="text-sm text-slate-600 mt-1">
                  <Clock className="h-4 w-4 inline mr-1" />
                  {formatDate(proof.timestamp)}
                </p>
              </div>
            </div>

            {/* Customer Details */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-[#003366] text-lg border-b pb-2">Customer Details</h3>
                <div>
                  <p className="text-sm text-slate-500">Customer Name</p>
                  <p className="font-medium text-slate-900">{proof.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{addressLabel}</p>
                  <p className="font-medium text-slate-900 flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />
                    {proof.delivery_address}
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-semibold text-[#003366] text-lg border-b pb-2">{detailsLabel}</h3>
                <div>
                  <p className="text-sm text-slate-500">{completedByLabel}</p>
                  <p className="font-medium text-slate-900 flex items-center gap-2">
                    <Truck className="h-4 w-4 text-slate-400" />
                    {proof.driver_name}
                  </p>
                </div>
                {proof.recipient_name && (
                  <div>
                    <p className="text-sm text-slate-500">Received By</p>
                    <p className="font-medium text-slate-900 flex items-center gap-2">
                      <User className="h-4 w-4 text-slate-400" />
                      {proof.recipient_name}
                    </p>
                  </div>
                )}
                {proof.latitude && proof.longitude && (
                  <div>
                    <p className="text-sm text-slate-500">GPS Location</p>
                    <p className="font-mono text-sm text-slate-700">
                      {proof.latitude.toFixed(6)}, {proof.longitude.toFixed(6)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Driver Notes */}
            {proof.driver_notes && (
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-[#003366] mb-2">Driver Notes</h3>
                <p className="text-slate-700">{proof.driver_notes}</p>
              </div>
            )}

            {/* Items Description */}
            {job?.items_description && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-[#003366] mb-2 flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  {itemsLabel}
                </h3>
                <p className="text-slate-700 whitespace-pre-wrap">{job.items_description}</p>
              </div>
            )}

            {/* Special Instructions */}
            {job?.special_instructions && (
              <div className="mb-6 p-4 bg-amber-50 rounded-lg">
                <h3 className="font-semibold text-[#003366] mb-2">Special Instructions</h3>
                <p className="text-slate-700">{job.special_instructions}</p>
              </div>
            )}

            {/* Attached Documents - with upload capability */}
            <div className="mb-6 p-4 bg-emerald-50 rounded-lg print:bg-white print:border print:border-emerald-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[#003366] flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Attached Documents ({job?.documents?.length || 0})
                </h3>
                <div className="print:hidden">
                  <input
                    type="file"
                    onChange={handleDocumentUpload}
                    className="hidden"
                    id="doc-upload-docket"
                    disabled={uploading}
                  />
                  <label htmlFor="doc-upload-docket">
                    <Button asChild variant="outline" size="sm" disabled={uploading} className="cursor-pointer">
                      <span>
                        <Plus className="h-4 w-4 mr-1" />
                        {uploading ? "Uploading..." : "Add Document"}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
              {(!job?.documents || job.documents.length === 0) ? (
                <p className="text-sm text-slate-500 py-2 text-center">No documents attached yet</p>
              ) : (
                <div className="space-y-2">
                  {job.documents.map((doc, index) => (
                    <div key={doc.id || index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-emerald-200">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-emerald-600" />
                        <span className="text-slate-700">{doc.filename}</span>
                      </div>
                      <div className="flex gap-1 print:hidden">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadDocument(doc)}
                          className="text-emerald-600 hover:text-emerald-800"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                        {user?.role === "admin" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDocumentDelete(doc)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Additional Photos - with upload capability */}
            <div className="mb-6 p-4 bg-amber-50 rounded-lg print:bg-white print:border print:border-amber-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[#003366] flex items-center gap-2">
                  <Camera className="h-5 w-5" />
                  Additional Photos ({job?.photos?.length || 0})
                </h3>
                <div className="print:hidden">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload-docket"
                    disabled={uploading}
                  />
                  <label htmlFor="photo-upload-docket">
                    <Button asChild variant="outline" size="sm" disabled={uploading} className="cursor-pointer">
                      <span>
                        <Plus className="h-4 w-4 mr-1" />
                        {uploading ? "Uploading..." : "Add Photo"}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
              {(!job?.photos || job.photos.length === 0) ? (
                <p className="text-sm text-slate-500 py-2 text-center">No additional photos yet</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {job.photos.map((photo, index) => (
                    <div key={photo.id || index} className="relative group">
                      <div className="border border-amber-200 rounded-lg overflow-hidden bg-white">
                        <img
                          src={`data:${photo.content_type};base64,${photo.data}`}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                      </div>
                      {user?.role === "admin" && (
                        <button
                          onClick={() => handlePhotoDelete(photo)}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity print:hidden"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                      <p className="text-xs text-slate-500 mt-1 truncate">By: {photo.uploaded_by}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Signature & Photo */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-[#003366] text-lg border-b pb-2 mb-4">Customer Signature</h3>
                <div className="border border-slate-200 rounded-lg p-2 bg-white">
                  <img
                    src={proof.signature_data}
                    alt="Customer Signature"
                    className="w-full max-h-48 object-contain"
                    data-testid="signature-image"
                  />
                </div>
              </div>
              
              {proof.photo_data && (
                <div>
                  <h3 className="font-semibold text-[#003366] text-lg border-b pb-2 mb-4">{photoLabel}</h3>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <img
                      src={proof.photo_data}
                      alt={photoLabel}
                      className="w-full max-h-48 object-cover"
                      data-testid="photo-image"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirmation Footer */}
            <div className="mt-8 pt-6 border-t-2 border-[#003366]">
              <div className="flex items-center justify-center gap-2 text-emerald-600">
                <CheckCircle className="h-6 w-6" />
                <span className="font-semibold text-lg">{confirmedLabel}</span>
              </div>
              <p className="text-center text-sm text-slate-500 mt-2">
                This document serves as proof of {jobTypeLabel.toLowerCase()} for the above order.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email {jobTypeLabel} Confirmation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Recipient Email</Label>
              <Input
                id="email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="customer@example.com"
                data-testid="email-input"
              />
            </div>
            <p className="text-sm text-slate-500">
              The {jobTypeLabel.toLowerCase()} confirmation will be sent with signature and photo attached.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEmail}
              disabled={sending}
              className="bg-[#003366] hover:bg-[#002244]"
              data-testid="send-email-btn"
            >
              {sending ? "Sending..." : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
